import {InputText} from '@/components/commons/input-text/InputText'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {useTranslation} from '@/translations/i18n'
import dayjs from 'dayjs'
import {ChangeEvent} from 'react'
import {useController, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'

const ProjectDurationFieldSchema = ProjectFieldsSchema.pick({
    durationDays: true,
    startDate: true,
    expirationDate: true
})
type ProjectDurationFieldSchema = z.infer<typeof ProjectDurationFieldSchema>

export const ProjectDurationField = () => {
    const {t} = useTranslation()
    const {control} = useFormContext<ProjectDurationFieldSchema>()
    const {field, fieldState} = useController({control, name: 'durationDays'})
    const {field: expirationDateField} = useController({control, name: 'expirationDate'})
    const startDate = useWatch({control, name: 'startDate'})

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        if (value == '') {
            field.onChange(null)
            expirationDateField.onChange(null)
        } else {
            const durationInDays = parseInt(value)
            if (!isNaN(durationInDays)) {
                field.onChange(durationInDays)
                const calculatedEndDate = dayjs(startDate).add(durationInDays, 'day').toDate()
                expirationDateField.onChange(calculatedEndDate)
            }
        }
    }

    return (
        <InputText
            {...field}
            type="number"
            label={t('projects:project_duration')}
            placeholder={t('projects:start_project_date_placeholder')}
            errorMessage={fieldState.error?.message}
            value={field.value ?? ''}
            onChange={handleChange}
            disabled={!startDate}
        />
    )
}
